import React, { useState, useMemo } from "react";
import { Phone, Search, User, Home, Mail, Calendar, Flame } from "lucide-react";
import { formatDate } from "../../utils/dateFormat";

/**
 * BBQResidentContacts - Click-to-call contact cards for BBQ bookings
 */
const BBQResidentContacts = ({ bookings }) => {
  const [searchQuery, setSearchQuery] = useState("");
  
  // Filter out cancelled bookings and apply search
  const filteredBookings = useMemo(() => {
    const active = bookings.filter(b => b.status !== 'cancelled');
    
    if (!searchQuery.trim()) return active;
    
    const query = searchQuery.toLowerCase();
    return active.filter(b => 
      b.name?.toLowerCase().includes(query) ||
      b.booking_code?.toLowerCase().includes(query) ||
      b.block_unit?.toLowerCase().includes(query) ||
      b.mobile?.includes(query) ||
      b.email?.toLowerCase().includes(query)
    );
  }, [bookings, searchQuery]);

  const getPitLabel = (pit) => {
    if (pit === 'both') return 'Both Pits';
    return `Pit ${pit?.slice(-1)}`;
  };

  const getSlotTime = (slot) => {
    return slot === 'afternoon' ? '1PM - 5PM' : '6PM - 10PM';
  };

  return (
    <div className="brutal-card">
      <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
        <Phone className="text-orange-500" /> Resident Contacts
      </h3>
      
      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        <input
          type="text"
          placeholder="Search residents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border-2 border-black"
        />
      </div>
      
      {/* Contact Cards */}
      {filteredBookings.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No resident contacts found</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBookings.map(booking => (
            <div key={booking.booking_id} className="border-2 border-black p-4 bg-white">
              <div className="flex justify-between items-start mb-3">
                <span className="font-mono font-bold text-orange-600 notranslate" translate="no">
                  {booking.booking_code}
                </span>
                <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                  booking.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {booking.status?.toUpperCase()}
                </span>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-gray-400" />
                  <span className="font-bold notranslate" translate="no">{booking.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Home size={16} className="text-gray-400" />
                  <span className="text-sm notranslate" translate="no">{booking.block_unit}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail size={16} className="text-gray-400" />
                  <a 
                    href={`mailto:${booking.email}`} 
                    className="text-sm text-blue-600 hover:underline truncate notranslate" 
                    translate="no"
                  >
                    {booking.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  <span className="text-sm font-mono">{formatDate(booking.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Flame size={16} className="text-orange-500" />
                  <span className="text-sm">{getPitLabel(booking.pit_selection)} • {getSlotTime(booking.slot_type)}</span>
                </div>
              </div>
              
              {/* Click-to-Call Button */}
              <a 
                href={`tel:${booking.mobile}`}
                className="flex items-center justify-center gap-2 w-full py-3 bg-green-500 text-white font-bold text-sm border-2 border-black hover:bg-green-600 transition-all"
              >
                <Phone size={18} /> CALL <span className="notranslate" translate="no">{booking.mobile}</span>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BBQResidentContacts;
